import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../api/client.js";
import type { ImageBatchListItem, ImagePersonaOption } from "../../../api/types.js";

const REFRESH_INTERVAL_MS = 15_000;
const NON_TERMINAL = new Set(["queued", "running"]);

/** Where opening one Image request goes: the SAME request detail, inside the main REVIEWS or HISTORY area (HISTORY is the default). */
export function imageRequestDetailPath(batchId: string, area: "reviews" | "history" = "history"): string {
  return `/generate/${area}/image/${encodeURIComponent(batchId)}`;
}

/** What a REVIEW row says about where the request is - it is in REVIEW for ALL of these states, until the user's final decision. */
export function describeReviewState(status: ImageBatchListItem["status"]): string {
  switch (status) {
    case "queued":
      return "Queued";
    case "running":
      return "Generating";
    case "completed":
      return "Ready — awaiting your decision";
    case "completed_partial":
      return "Partially completed — awaiting your decision";
    case "failed":
      return "Could not be produced — awaiting your decision";
  }
}

/**
 * Image requests inside the shared, system-level REVIEWS area (Image Mode's lifecycle: request -> REVIEW -> final user decision -> HISTORY).
 * ONE row per request however many images (1-15) it holds - never one row per image. It lists the requests with no final decision yet
 * (queued, generating, partially completed, or finished-but-undecided); the server does the split (`?review=pending`), so this list never
 * derives a lifecycle state. Decided requests live in the unified HISTORY list (`HistoryTab`), next to Videos.
 *
 * It degrades quietly: if the Image API cannot be reached the section shows a short note and nothing else on the page (Video REVIEWS) is affected.
 */
export function ImageRequestsList({ tenantId }: { readonly tenantId: string }) {
  const [requests, setRequests] = useState<ImageBatchListItem[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [personaNames, setPersonaNames] = useState<Record<string, string>>({});

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const result = await api.get<{ batches: ImageBatchListItem[] }>(`/images/${encodeURIComponent(tenantId)}/batches?review=pending`);
      setRequests(result.batches);
      setUnavailable(false);
    } catch {
      setUnavailable(true);
    }
  }, [tenantId]);

  useEffect(() => {
    void refresh();
    api
      .get<{ personas: ImagePersonaOption[] }>(`/images/${encodeURIComponent(tenantId)}/personas`)
      .then((result) => setPersonaNames(Object.fromEntries(result.personas.map((p) => [p.personaId, p.displayName]))))
      .catch(() => undefined);
  }, [refresh, tenantId]);

  const anyGenerating = requests?.some((r) => NON_TERMINAL.has(r.status)) === true;
  useEffect(() => {
    if (!anyGenerating) return;
    const interval = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [anyGenerating, refresh]);

  if (requests === null) {
    return <p className="muted">{unavailable ? "Image requests are unavailable right now." : "Loading…"}</p>;
  }
  if (requests.length === 0) {
    return (
      <p className="muted" data-testid="image-requests-review-empty">
        No Image requests are waiting for review.
      </p>
    );
  }

  return (
    <div data-testid="image-requests-review">
      {unavailable && <p className="muted">Image requests could not be refreshed just now.</p>}
      <table>
        <thead>
          <tr>
            <th>Type</th>
            <th>Source</th>
            <th>Created</th>
            <th>Persona</th>
            <th>Images</th>
            <th>State</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id} data-testid={`image-request-row-${r.id}`}>
              <td>
                <span className="badge">Image request</span>
              </td>
              <td>
                <img src={r.sourcePreviewUrl} alt="Source" style={{ width: 40, height: 56, objectFit: "cover", borderRadius: 4, background: "var(--surface-2)" }} />
              </td>
              <td>{new Date(r.createdAt).toLocaleString()}</td>
              <td>{personaNames[r.personaId] ?? r.personaId}</td>
              <td>
                {r.deliveredCount} of {r.requestedCount}
              </td>
              <td>
                <span className={`badge status-${r.status}`} data-testid={`image-request-state-${r.id}`}>
                  {describeReviewState(r.status)}
                </span>
              </td>
              <td>
                <Link to={imageRequestDetailPath(r.id, "reviews")}>Open</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
